import { Ionicons } from "@expo/vector-icons";
import { DocumentPickerAsset } from "expo-document-picker";
import { router, useFocusEffect } from "expo-router";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Text, View, ScrollView, ActivityIndicator, Image } from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import ButtonSmall from "@/components/button/ButtonSmall";
import ButtonUploadDoc from "@/components/button/ButtonUploadDoc";
import PhotoInstructionsModal from "@/components/modal/onboarding/PhotoInstructionsModal";
import UploadModal from "@/components/modal/onboarding/UploadModal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { sendRequest, sendRequestFormData, baseURL } from "@/utils/ApiUtil";
import { toastInstance } from "@/utils/ToastConfig";
import {
  requiredCorporationDocs,
  requiredIndividualCorporateDocs,
  requiredIndividualDocs,
  personalDocuments,
  imageFileTypes,
  defaultFileTypes,
  corporatePdfDocuments,
  pdfFileTypes,
} from "@/utils/onboarding/OnboardingUtils";
import { transformToReadable } from "@/utils/text/FormatTextUtils";

export default function Documents() {
  const [docIsUploading, setDocIsUploading] = useState<{
    [key: string]: boolean;
  }>({});
  const [isUploadModalVisible, setIsUploadModalVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const [docSent, setDocSent] = useState<{ [key: string]: boolean }>({});
  const [request, setRequest] = useState(true);
  const [readyForSubmission, setReadyForSubmission] = useState(false);
  const [isDocumentChange, setIsDocumentChange] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [application, setApplication] = useState<any>(null);
  const [applicationType, setApplicationType] = useState("");
  const [currentDocuments, setCurrentDocuments] = useState([]);
  const [individualDocumentsWithError, setIndividualDocumentsWithError] =
    useState([]);
  const [isAddIndividualFlow, setIsAddIndividualFlow] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { handleSubmit, formState } = useForm<FormData>({
    mode: "onChange",
  });
  const { isSubmitting } = formState;

  const state = useAppSelector((state: any) => state);

  useFocusEffect(
    React.useCallback(() => {
      resetAllStates();
      setIsAddIndividualFlow(state.isAddIndividualFlow);
      fetchDocuments(state.isAddIndividualFlow);

      return () => {
        setShowModal(false);
        dispatch(setForm({ isAddIndividualFlow: false }));
      };
    }, [state.isAddIndividualFlow])
  );

  const fetchDocuments = async (isAddIndividualFlow?: boolean) => {
    try {
      const getApplication = await sendRequest(
        "get",
        "/applications",
        null,
        true
      );
      setApplication(getApplication);

      const localIdToRequest = getApplication?.application[0]?.id;
      const localApplicationType =
        getApplication?.application[0]?.applicationType;

      storage.set("user.auth.applicationId", localIdToRequest);
      storage.set("user.auth.applicationType", localApplicationType);
      setApplicationType(localApplicationType);

      if (getApplication) {
        const response = await sendRequest(
          "get",
          "/application-status",
          { applicationId: localIdToRequest },
          true
        );

        if (response.status === "CHANGES_REQUESTED") {
          setIsDocumentChange(true);
        }
        if (response.status === "READY_FOR_SUBMISSION") {
          setReadyForSubmission(true);
        }

        const currentCorporationIndividualId = state.corporationIndividualId;
        const individualDocumentsFilter = [];

        response.application_individual_errors.forEach((individualError) => {
          const hasChangesRequested = individualError.document_errors.some(
            (error) => error.status === "CHANGES_REQUESTED"
          );

          const hasNoSubmittedError = !individualError.document_errors.some(
            (error) =>
              error.status === "SUBMITTED" &&
              !["ACCOUNT_AGREEMENT", "LAYER2_ACCOUNT_AGREEMENT"].includes(
                error.document
              )
          );

          const hasUploadedRequiredDocs = individualError.document_errors.some(
            (error) => {
              if (
                (error.document === "PASSPORT" &&
                  error.status !== "SUBMITTED" &&
                  individualError.document_errors.some(
                    (innerError) =>
                      innerError.document === "IDENTITY_CARD_BACK" &&
                      innerError.status !== "SUBMITTED"
                  ) &&
                  individualError.document_errors.some(
                    (innerError) =>
                      innerError.document === "IDENTITY_CARD_FRONT" &&
                      innerError.status !== "SUBMITTED"
                  )) ||
                (error.document === "IDENTITY_CARD_BACK" &&
                  error.status !== "SUBMITTED" &&
                  individualError.document_errors.some(
                    (innerError) =>
                      innerError.document === "PASSPORT" &&
                      innerError.status !== "SUBMITTED"
                  )) ||
                (error.document === "IDENTITY_CARD_FRONT" &&
                  error.status !== "SUBMITTED" &&
                  individualError.document_errors.some(
                    (innerError) =>
                      innerError.document === "PASSPORT" &&
                      innerError.status !== "SUBMITTED"
                  )) ||
                (error.document === "SELFIE" && error.status !== "SUBMITTED")
              ) {
                return true;
              }
              return false;
            }
          );

          if (
            hasChangesRequested ||
            hasNoSubmittedError ||
            hasUploadedRequiredDocs
          ) {
            individualError.document_errors.forEach((error) => {
              if (
                (error.status === "CHANGES_REQUESTED" ||
                  error.status === "MISSING") &&
                error.document !== "ACCOUNT_AGREEMENT" &&
                error.document !== "LAYER2_ACCOUNT_AGREEMENT"
              ) {
                individualDocumentsFilter.push({
                  individual_id: individualError.individual_id,
                  document_id: error.document_id,
                  status: error.status,
                  document: error.document,
                });
              }
            });
          }
        });
        setIndividualDocumentsWithError(individualDocumentsFilter);

        const individualWithErrorId =
          individualDocumentsFilter.length > 0
            ? individualDocumentsFilter[0].individual_id
            : response.application_individual_errors[0].individual_id;

        const individualIdToFind = isAddIndividualFlow
          ? currentCorporationIndividualId
          : individualWithErrorId;

        const individualApplicationErrors =
          response.application_individual_errors.find(
            (individual) => individual.individual_id === individualIdToFind
          );

        const individualDocuments =
          individualApplicationErrors?.document_errors;

        const corporationDocumentErrors = response.application_document_errors;
        const filterCorporationDocuments = corporationDocumentErrors.filter(
          (document) => document.status !== "SUBMITTED"
        );
        const filterIndividualDocuments = individualDocuments?.filter(
          (document) => document.status !== "SUBMITTED"
        );

        const documentErrors =
          !isAddIndividualFlow && localApplicationType === "CORPORATION"
            ? filterCorporationDocuments
            : filterIndividualDocuments;

        setCurrentDocuments(documentErrors);
        setRequest(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //Control when the individual documents are already uploaded and it's finished.
  useEffect(() => {
    if (isDocumentChange) {
      toastInstance("error", t("common:toast.subtitle.documentschange"));
    }
    if (isAddIndividualFlow) {
      if (
        docSent["PASSPORT-default"] &&
        docSent["SELFIE-default"] &&
        docSent["BANK_STATEMENT-default"] &&
        docSent["IDENTITY_CARD_BACK-default"] &&
        docSent["IDENTITY_CARD_FRONT-default"]
      ) {
        dispatch(setForm({ addIndividualAdded: true }));
        router.replace("/corporation/addindividual");
      }
    }
  }, [isDocumentChange, isAddIndividualFlow, docSent]);

  const resetAllStates = () => {
    setDocIsUploading({});
    setDocSent({});
    setIsUploadModalVisible({});
    setCurrentDocuments([]);
    setIndividualDocumentsWithError([]);
    setApplication(null);
    setRequest(true);
    setReadyForSubmission(false);
    setIsDocumentChange(false);
    setShowModal(true);
    setIsAddIndividualFlow(false);
  };

  //Call to submit application
  const onSubmit = async () => {
    const applicationId = storage.getString("user.auth.applicationId");
    try {
      const response = await sendRequest(
        "post",
        "/submit-application",
        { applicationId },
        true
      );
      if (response) {
        dispatch(setForm({ applicationReady: false }));
        router.push("/finish");
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const fetchApplicationStatus = async (applicationId: string) => {
    try {
      const request = await sendRequest(
        "get",
        "/application-status",
        { applicationId },
        true
      );

      if (request.status === "READY_FOR_SUBMISSION") {
        toastInstance(
          "success",
          t("common:toast.subtitle.applicationreadyforsubmit")
        );
        setReadyForSubmission(true);
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleDocument = async (
    file: DocumentPickerAsset,
    item?: string,
    individualId?: string
  ) => {
    const token = storage.getString("user.auth.token");
    const applicationId = storage.getString("user.auth.applicationId");

    const handleUploadDocument = async (
      file: DocumentPickerAsset,
      item?: string,
      individualId?: string
    ) => {
      const matchingDocument = currentDocuments.find(
        (doc) => doc.document === item
      );

      const documentId = individualDocumentsWithError.find(
        (error) =>
          error.document === item && error.individual_id === individualId
      )?.document_id
        ? individualDocumentsWithError.find(
            (error) =>
              error.document === item && error.individual_id === individualId
          )?.document_id
        : matchingDocument.document_id;

      try {
        setDocIsUploading((prevState) => ({
          ...prevState,
          [`${item}-${individualId || "default"}`]: true,
        }));
        const request = await sendRequestFormData(
          `${baseURL}/upload-documents`,
          documentId,
          token,
          file
        );
        if (request) {
          setDocSent((prevState) => ({
            ...prevState,
            [`${item}-${individualId || "default"}`]: true,
          }));
          setDocIsUploading((prevState) => ({
            ...prevState,
            [`${item}-${individualId || "default"}`]: false,
          }));
          if (!isAddIndividualFlow) {
            fetchApplicationStatus(applicationId);
          }
        }
      } catch (error: any) {
        setDocIsUploading((prevState) => ({
          ...prevState,
          [`${item}-${individualId || "default"}`]: false,
        }));
        console.error(error);
      }
    };

    handleUploadDocument(file, item, individualId);
  };

  function renderDocuments(defaultDocuments: any) {
    const requiredDocs =
      !isAddIndividualFlow && applicationType === "CORPORATION"
        ? requiredCorporationDocs
        : applicationType === "INDIVIDUAL"
          ? requiredIndividualDocs
          : requiredIndividualCorporateDocs;

    const sortDocuments = (a: any, b: any) => {
      const aIsRequired = requiredDocs.includes(a.document);
      const bIsRequired = requiredDocs.includes(b.document);

      if (aIsRequired && !bIsRequired) {
        return -1;
      }
      if (!aIsRequired && bIsRequired) {
        return 1;
      }
      return a.document.localeCompare(b.document);
    };

    const sortedDocuments = defaultDocuments.sort(sortDocuments);

    return sortedDocuments.map((doc, index) => {
      if (
        (doc.document === "DRIVERS_LICENCE_FRONT" ||
          doc.document === "DRIVERS_LICENCE_BACK") &&
        !state.isUSApplication
      ) {
        return;
      }
      return (
        <View
          key={index}
          className="flex-row items-center justify-between w-full m-3 border-b border-gray py-1"
        >
          <View className="flex-row w-fit justify-start">
            <Text className="text-lg">{transformToReadable(doc.document)}</Text>
            {requiredDocs.includes(doc.document) && (
              <Text className="text-red text-lg">*</Text>
            )}
          </View>
          <View className="w-fit items-center">
            {docSent[`${doc.document}-default`] ? (
              <View className="items-center min-w-64">
                <Ionicons name="checkmark-circle" color="green" size={32} />
              </View>
            ) : docIsUploading[`${doc.document}-default`] ? (
              <View className="items-center min-w-64">
                <ActivityIndicator size="large" color="#5D0CBA" />
              </View>
            ) : (
              <>
                {personalDocuments.includes(doc.document) ? (
                  <View className="flex-col gap-2 items-center min-w-64">
                    <ButtonSmall
                      label={t("common:button.upload")}
                      onClick={() =>
                        setIsUploadModalVisible((prevState) => ({
                          ...prevState,
                          [`${doc.document}-${"default"}`]: true,
                        }))
                      }
                    />
                    <View className="flex-row gap-0.5 items-center">
                      <Text className="text-sm text-darkergray">
                        {t("common:terms.acceptedfiles")}:{" "}
                      </Text>
                      <Text className="font-medium text-sm text-darkergray">{`${imageFileTypes.join(
                        ", "
                      )}`}</Text>
                    </View>
                    <UploadModal
                      visible={
                        isUploadModalVisible[`${doc.document}-default`] === true
                      }
                      onClose={() =>
                        setIsUploadModalVisible((prevState) => ({
                          ...prevState,
                          [`${doc.document}-default`]: false,
                        }))
                      }
                      onUpload={handleDocument}
                      documentType={doc.document}
                      item={doc.document}
                      onlyImage
                    />
                  </View>
                ) : (
                  <View className="flex-col gap-2 items-center min-w-64">
                    <ButtonUploadDoc
                      label={t("common:button.upload")}
                      onClick={handleDocument}
                      item={doc.document}
                      onlyPdf={corporatePdfDocuments.includes(doc.document)}
                    />
                    <View className="flex-row gap-0.5 items-center">
                      <Text className="text-sm text-darkergray">
                        {t("common:terms.acceptedfiles")}:{" "}
                      </Text>
                      {corporatePdfDocuments.includes(doc.document) ? (
                        <Text className="font-medium text-sm text-darkergray">{`${pdfFileTypes.join(
                          ", "
                        )}`}</Text>
                      ) : (
                        <Text className="font-medium text-sm text-darkergray">{`${defaultFileTypes.join(
                          ", "
                        )}`}</Text>
                      )}
                    </View>
                  </View>
                )}
              </>
            )}
          </View>
        </View>
      );
    });
  }

  function renderIndividualDocumentsWithError(defaultDocuments: any[]) {
    const documentsByIndividualId: Record<string, any[]> = {};
    const individuals =
      application?.application[0]?.customerDetailsCorporation?.individuals ||
      [];

    const individualNameMap: Record<string, string> = individuals.reduce(
      (acc: Record<string, string>, individual: any) => {
        acc[individual.individualId] = `${individual.firstName || "N/A"} ${
          individual.lastName || "N/A"
        }`;
        return acc;
      },
      {}
    );

    if (defaultDocuments) {
      defaultDocuments.forEach((doc) => {
        if (!documentsByIndividualId[doc.individual_id]) {
          documentsByIndividualId[doc.individual_id] = [];
        }
        documentsByIndividualId[doc.individual_id].push(doc);
      });
    }

    return Object.entries(documentsByIndividualId).map(
      ([individualId, documents], index) => (
        <View key={index} className="w-full my-4 border-b border-black1">
          <View className="w-full items-center">
            <Text className="text-2xl">
              {individualNameMap[individualId] || "N/A"}
            </Text>
          </View>
          <View className="w-full">
            {Array.isArray(documents) &&
              documents
                .filter(
                  (doc) =>
                    !["DRIVERS_LICENCE_BACK", "DRIVERS_LICENCE_FRONT"].includes(
                      doc.document
                    )
                )
                .map((doc, index) => (
                  <View
                    key={index}
                    className="flex-row items-center justify-between w-full m-3 border-b border-gray py-1"
                  >
                    <View className="flex-row w-fit justify-start">
                      <Text className="text-lg">
                        {transformToReadable(doc.document)}
                      </Text>
                      {requiredCorporationDocs.includes(doc.document) && (
                        <Text className="text-red text-lg">*</Text>
                      )}
                    </View>
                    <View className="w-44 items-center">
                      {docSent[`${doc.document}-${doc.individual_id}`] ? (
                        <View className="items-center min-w-64">
                          <Ionicons
                            name="checkmark-circle"
                            color="green"
                            size={32}
                          />
                        </View>
                      ) : docIsUploading[
                          `${doc.document}-${doc.individual_id}`
                        ] ? (
                        <View className="items-center min-w-64">
                          <ActivityIndicator size="large" color="#5D0CBA" />
                        </View>
                      ) : (
                        <>
                          {personalDocuments.includes(doc.document) ? (
                            <View className="flex-col gap-2 items-center min-w-64">
                              <ButtonSmall
                                label={t("common:button.upload")}
                                onClick={() =>
                                  setIsUploadModalVisible((prevState) => ({
                                    ...prevState,
                                    [`${doc.document}-${doc.individual_id}`]:
                                      true,
                                  }))
                                }
                              />
                              <View className="flex-row gap-0.5 items-center">
                                <Text className="text-sm text-darkergray">
                                  {t("common:terms.acceptedfiles")}:{" "}
                                </Text>
                                <Text className="font-medium text-sm text-darkergray">{`${imageFileTypes.join(
                                  ", "
                                )}`}</Text>
                              </View>
                              <UploadModal
                                visible={
                                  isUploadModalVisible[
                                    `${doc.document}-${doc.individual_id}`
                                  ] === true
                                }
                                onClose={() =>
                                  setIsUploadModalVisible((prevState) => ({
                                    ...prevState,
                                    [`${doc.document}-${doc.individual_id}`]:
                                      false,
                                  }))
                                }
                                onUpload={handleDocument}
                                documentType={doc.document}
                                individualId={doc.individual_id}
                                item={doc.document}
                                onlyImage
                              />
                            </View>
                          ) : (
                            <View className="flex-col gap-2 items-center min-w-64">
                              <ButtonUploadDoc
                                label={t("common:button.upload")}
                                onClick={handleDocument}
                                item={doc.document}
                                individualId={doc.individual_id}
                                onlyPdf={corporatePdfDocuments.includes(
                                  doc.document
                                )}
                              />
                              <View className="flex-row gap-0.5 items-center">
                                <Text className="text-sm text-darkergray">
                                  {t("common:terms.acceptedfiles")}:{" "}
                                </Text>
                                {corporatePdfDocuments.includes(
                                  doc.document
                                ) ? (
                                  <Text className="font-medium text-sm text-darkergray">{`${pdfFileTypes.join(
                                    ", "
                                  )}`}</Text>
                                ) : (
                                  <Text className="font-medium text-sm text-darkergray">{`${defaultFileTypes.join(
                                    ", "
                                  )}`}</Text>
                                )}
                              </View>
                            </View>
                          )}
                        </>
                      )}
                    </View>
                  </View>
                ))}
          </View>
        </View>
      )
    );
  }

  return (
    <ScrollView
      contentContainerClassName="flex-grow"
      style={{ backgroundColor: "#FFFFFF", padding: 8 }}
    >
      {request && (
        <ActivityIndicator
          size="large"
          color="#5D0CBA"
          className="z-50 bg-white1 absolute items-center justify-center w-full h-full"
        />
      )}
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <View className="items-center justify-center w-full h-full">
        <Logo className="m-2" />
        <View className="m-2">
          <Title
            text={
              isAddIndividualFlow
                ? t(`onboarding/corporation/documents:individualtitle`)
                : t("onboarding/corporation/documents:title")
            }
          />
        </View>
        <View className="w-full items-center">
          <Text className="text-darkergray text-center text-base">
            {isAddIndividualFlow
              ? t("onboarding/corporation/documents:individualsubtitle")
              : t("onboarding/corporation/documents:subtitle")}
          </Text>
          <Image
            source={require("@/assets/upload-documents.png")}
            style={{ height: 200, width: 250 }}
            resizeMode="contain"
          />
        </View>
        <View className="flex-row w-full items-center justify-center gap-2 mt-2">
          <Text className="text-base text-red">*</Text>
          <Text className="text-base text-midgray">{` - ${t(
            "common:terms.requireddocuments"
          )}`}</Text>
        </View>
        <View className="w-full flex-grow lg:w-3/5 lg:h-fit">
          {renderDocuments(currentDocuments)}
          {individualDocumentsWithError.length > 0 &&
            !isAddIndividualFlow &&
            applicationType === "CORPORATION" && (
              <View className="w-full my-4">
                <View className="flex-col w-full items-center">
                  <Text className="text-2xl text-darkergray">
                    {t(
                      "onboarding/corporation/documents:sections.individualdocuments"
                    )}
                  </Text>
                  <Text className="text-sm mt-2 text-midgray text-center">
                    {t(
                      "onboarding/corporation/documents:sections.individualdocumentssubtitle"
                    )}
                  </Text>
                </View>
                <View className="w-full">
                  {renderIndividualDocumentsWithError(
                    individualDocumentsWithError
                  )}
                </View>
              </View>
            )}
        </View>
        <View className="w-full items-center">
          <Button
            label={t("common:button.submitapplication")}
            onClick={handleSubmit(onSubmit)}
            blocked={!readyForSubmission}
            isLoading={isSubmitting}
          />
        </View>
        <View className="justify-end">
          <Copyright />
        </View>
      </View>
      <PhotoInstructionsModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        onClick={(state) => setShowModal(state)}
      />
    </ScrollView>
  );
}
